import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar,
  Package,
  FileText,
  AlertCircle,
  LayoutList,
  LayoutGrid,
  RefreshCw,
  Loader,
  Clock
} from 'lucide-react';
import { OrderHistoryDto, OrderLineHistoryDto, OrderTextDto } from '../../../../types/orderHistory';
import orderService from '../../../../services/orderService';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { OrderTimelineModal } from '../order-timeline';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow } from '../../../shared/table';

// Extended type for order history table
interface OrderHistoryTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  orderDate: string | null;
  deliveryDate: string | null;
  customerNo: string;
  customerName: string | null;
  // Leveringsadresse
  delCustomerName: string | null;
  delAddress1: string | null;
  delCity: string | null;
  delZipCode: string | null;
  // Referanser
  customerOrderNo: string | null;
  // Detaljer
  orderTotal: number | null;
  carrierName: string | null;
  warehouse: string | null;
  pListID: number | null;
  pickListNoERP: string | null;
  operatorId: string | null;
  operatorName: string | null;
  totalLines: number;
  totalQty: number;
  // Lines and texts
  lines: OrderLineHistoryDto[];
  orderTexts: OrderTextDto[];
  // Original
  _original: OrderHistoryDto;
}

// Extended type for flat order line table
interface OrderLineTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderLineNo: number;
  lineStatus: number;
  itemNo: string;
  itemName: string | null;
  qtyOrdered: number;
  qtyPicked: number;
  qtyDelivered: number;
  unit: string | null;
  batchID: string | null;
  locationNo: string | null;
  operatorId: string | null;
  operatorName: string | null;
  pListID: number | null;
  pickListNoERP: string | null;
  customerNo: string | null;
  customerName: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  _original: OrderLineHistoryDto;
}

// Create column factories
const orderColumnFactory = createColumnFactory<OrderHistoryTableRow>();
const lineColumnFactory = createColumnFactory<OrderLineTableRow>();

interface OrderHistoryProps {
  onBack?: () => void;
}

type ViewMode = 'master-detail' | 'flat';

export const OrderHistory: React.FC<OrderHistoryProps> = ({ onBack }) => {
  // State management
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [viewMode, setViewMode] = useState<ViewMode>('master-detail');
  const [orders, setOrders] = useState<OrderHistoryDto[]>([]);
  const [flatLines, setFlatLines] = useState<OrderLineHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timeline modal state
  const [timelineOrderNo, setTimelineOrderNo] = useState<number | null>(null);

  // Date filtering - use YYYY-MM-DD strings to avoid timezone issues
  const formatDateStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [fromDate, setFromDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default 7 days ago
    return formatDateStr(date);
  });
  const [toDate, setToDate] = useState<string>(formatDateStr(new Date()));
  const [dateError, setDateError] = useState<string | null>(null);

  // Helper: check if YYYY-MM-DD string is a valid date
  const isValidDateStr = (str: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(str);
  };

  // Validate date range - returns error message or null
  const validateDateRange = useCallback((from: string, to: string): string | null => {
    if (!isValidDateStr(from) || !isValidDateStr(to)) {
      return t('manager.modules.orderHistory.errors.invalidDate', {}, 'Ugyldig dato');
    }
    if (from > to) {
      return t('manager.modules.orderHistory.errors.fromAfterTo', {}, 'Fra-dato må være før til-dato');
    }
    const fromMs = new Date(from + 'T00:00:00').getTime();
    const toMs = new Date(to + 'T00:00:00').getTime();
    const daysDiff = Math.ceil((toMs - fromMs) / (1000 * 60 * 60 * 24));
    if (daysDiff > 30) {
      return t('manager.modules.orderHistory.errors.maxPeriod', {}, 'Maksimalt 30 dagers periode tillatt');
    }
    return null;
  }, [t]);

  // Update dateError for UI display
  useEffect(() => {
    setDateError(validateDateRange(fromDate, toDate));
  }, [fromDate, toDate, validateDateRange]);

  // Fetch data - triggered by Oppdater button, not automatically on date change
  const fetchData = useCallback(async () => {
    const validationError = validateDateRange(fromDate, toDate);
    if (validationError) return;

    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'master-detail') {
        const data = await orderService.getOrdersHistory(fromDate, toDate, currentWarehouse || undefined);
        setOrders(data);
      } else {
        const data = await orderService.getOrderLinesHistoryFlat(fromDate, toDate, currentWarehouse || undefined);
        setFlatLines(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.orderHistory.errors.generic', {}, 'En feil oppstod'));
    } finally {
      setLoading(false);
    }
  }, [viewMode, fromDate, toDate, currentWarehouse, validateDateRange, t]);

  // Only auto-fetch on initial mount
  const hasLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchData();
    }
  }, [fetchData]);

  // Transform orders to table data
  const orderTableData: OrderHistoryTableRow[] = useMemo(() => {
    return orders.map(order => ({
      id: order.orderNo,
      orderNo: order.orderNo,
      orderNoERP: order.orderNoERP || order.orderNo.toString(),
      orderStatus: order.orderStatus,
      orderDate: order.orderDate || null,
      deliveryDate: order.deliveryDate || null,
      customerNo: order.customerNo,
      customerName: order.customerName || null,
      delCustomerName: order.delCustomerName || null,
      delAddress1: order.delAddress1 || null,
      delCity: order.delCity || null,
      delZipCode: order.delZipCode || null,
      customerOrderNo: order.customerOrderNo || null,
      orderTotal: order.orderTotal ?? null,
      carrierName: order.carrierName || null,
      warehouse: order.warehouse || null,
      pListID: order.pListID ?? null,
      pickListNoERP: order.pickListNoERP || null,
      operatorId: order.operatorId || null,
      operatorName: order.operatorName || null,
      totalLines: order.totalLines,
      totalQty: order.totalQty,
      lines: order.lines || [],
      orderTexts: order.orderTexts || [],
      _original: order
    }));
  }, [orders]);

  // Transform flat lines to table data
  const lineTableData: OrderLineTableRow[] = useMemo(() => {
    return flatLines.map((line, idx) => ({
      id: idx,
      orderNo: line.orderNo,
      orderNoERP: line.orderNoERP || line.orderNo.toString(),
      orderLineNo: line.orderLineNo,
      lineStatus: line.lineStatus,
      itemNo: line.itemNo,
      itemName: line.itemName || null,
      qtyOrdered: line.qtyOrdered,
      qtyPicked: line.qtyPicked,
      qtyDelivered: line.qtyDelivered,
      unit: line.unit || null,
      batchID: line.batchID || null,
      locationNo: line.locationNo || null,
      operatorId: line.operatorId || null,
      operatorName: line.operatorName || null,
      pListID: line.pListID ?? null,
      pickListNoERP: line.pickListNoERP || null,
      customerNo: line.customerNo || null,
      customerName: line.customerName || null,
      orderDate: line.orderDate || null,
      deliveryDate: line.deliveryDate || null,
      _original: line
    }));
  }, [flatLines]);

  // Row ID functions
  const getOrderRowId = useCallback((row: OrderHistoryTableRow) => row.orderNo, []);
  const getLineRowId = useCallback((row: OrderLineTableRow) => row.id, []);


  // Kode-baserte, språk-bevisste filteropsjoner for status-kolonnene (StatusType 1, ordre+linje).
  const statusOptions = useStatusOptions(1);

  // Order columns based on legacy OrdersHistory.dfm
  const orderColumns: ColumnDefinition<OrderHistoryTableRow>[] = useMemo(() => [
    // === TIDSLINJE-KNAPP ===
    orderColumnFactory.createTextColumn('_timeline' as any, '_timeline' as any, '', {
      width: 44,
      sortable: false,
      renderer: (_value, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setTimelineOrderNo(row.orderNo); }}
          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
          title={t('manager.modules.orderHistory.buttons.showTimeline', {}, 'Vis ordretidslinje')}
        >
          <Clock size={18} />
        </button>
      )
    }),
    // === HOVEDFELTER (synlige) ===
    orderColumnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.orderHistory.columns.orderNoERP', {}, 'Ordrenr. (ERP)'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createNumberColumn('orderNo', 'orderNo', t('manager.modules.orderHistory.columns.orderNoInternal', {}, 'Internt ordrenr.'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null && value !== undefined ? value.toString() : '-'
    }),
    orderColumnFactory.createTextColumn('orderStatus', 'orderStatus', t('manager.modules.orderHistory.columns.orderStatus', {}, 'Ordrestatus'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: statusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.orderStatus} statusType={1} />
    }),
    orderColumnFactory.createTextColumn('customerNo', 'customerNo', t('manager.modules.orderHistory.columns.customerNo', {}, 'Kundenr'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createTextColumn('customerName', 'customerName', t('warehouse.general.customerName', {}, 'Kundenavn'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createDateColumn('orderDate', 'orderDate', t('manager.modules.orderHistory.columns.orderDate', {}, 'Ordredato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.orderHistory.columns.deliveryDate', {}, 'Leveringsdato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.orderHistory.columns.pickListNo', {}, 'Plukklistenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    orderColumnFactory.createTextColumn('pickListNoERP', 'pickListNoERP', t('manager.modules.orderHistory.columns.pickListNoERP', {}, 'Plukklistenr. (ERP)'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createTextColumn('warehouse', 'warehouse', t('manager.modules.orderHistory.columns.warehouse', {}, 'Lagersted'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createTextColumn('carrierName', 'carrierName', t('warehouse.general.carrier', {}, 'Transportør'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createNumberColumn('totalLines', 'totalLines', t('manager.modules.orderHistory.columns.totalLines', {}, 'Ant. linjer'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createNumberColumn('totalQty', 'totalQty', t('manager.modules.orderHistory.columns.totalQty', {}, 'Totalt ant.'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(0) : '-'
    }),

    // === LEVERINGSADRESSE (skjult) ===
    orderColumnFactory.createTextColumn('delCustomerName', 'delCustomerName', t('manager.modules.orderHistory.columns.delCustomerName', {}, 'Lev. kundenavn'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delAddress1', 'delAddress1', t('manager.modules.orderHistory.columns.delAddress1', {}, 'Lev. adr. 1'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delZipCode', 'delZipCode', t('manager.modules.orderHistory.columns.delZipCode', {}, 'Lev. postnr'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delCity', 'delCity', t('manager.modules.orderHistory.columns.delCity', {}, 'Lev. poststed'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === REFERANSER (skjult) ===
    orderColumnFactory.createTextColumn('customerOrderNo', 'customerOrderNo', t('manager.modules.orderHistory.columns.customerOrderNo', {}, 'Oppr. bestnr'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === OPERATØR (skjult) ===
    orderColumnFactory.createTextColumn('operatorName', 'operatorName', t('manager.modules.orderHistory.columns.operator', {}, 'Operatør'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === ORDRESUM (skjult) ===
    orderColumnFactory.createNumberColumn('orderTotal', 'orderTotal', t('manager.modules.orderHistory.columns.orderTotal', {}, 'Ordresum'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    })
  ], [statusOptions, t]);

  // Flat line columns
  const lineColumns: ColumnDefinition<OrderLineTableRow>[] = useMemo(() => [
    lineColumnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.orderHistory.columns.orderNoERP', {}, 'Ordrenr. (ERP)'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createNumberColumn('orderNo', 'orderNo', t('manager.modules.orderHistory.columns.orderNoInternal', {}, 'Internt ordrenr.'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null && value !== undefined ? value.toString() : '-'
    }),
    lineColumnFactory.createNumberColumn('orderLineNo', 'orderLineNo', t('manager.modules.orderHistory.columns.lineNo', {}, 'Linjenr'), {
      width: 70,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createTextColumn('customerName', 'customerName', t('manager.modules.orderHistory.columns.customer', {}, 'Kunde'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.orderHistory.columns.itemNo', {}, 'Varenr.'), {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createTextColumn('itemName', 'itemName', t('manager.modules.orderHistory.columns.itemName', {}, 'Varebeskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createNumberColumn('qtyOrdered', 'qtyOrdered', t('manager.modules.orderHistory.columns.qtyOrdered', {}, 'Ant. best.'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createNumberColumn('qtyPicked', 'qtyPicked', t('manager.modules.orderHistory.columns.qtyPicked', {}, 'Ant. plukket'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createNumberColumn('qtyDelivered', 'qtyDelivered', t('manager.modules.orderHistory.columns.qtyDelivered', {}, 'Ant. levert'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createTextColumn('lineStatus', 'lineStatus', t('common.fields.status', {}, 'Status'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: statusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.lineStatus} statusType={1} />
    }),
    lineColumnFactory.createTextColumn('unit', 'unit', t('manager.modules.orderHistory.columns.unit', {}, 'Enhet'), {
      width: 70,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.orderHistory.columns.pickList', {}, 'Plukkliste'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    lineColumnFactory.createTextColumn('pickListNoERP', 'pickListNoERP', t('manager.modules.orderHistory.columns.pickListNoERP', {}, 'Plukklistenr. (ERP)'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('locationNo', 'locationNo', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('operatorName', 'operatorName', t('manager.modules.orderHistory.columns.operator', {}, 'Operatør'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('batchID', 'batchID', t('manager.modules.orderHistory.columns.batchNo', {}, 'Batchnr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    lineColumnFactory.createDateColumn('orderDate', 'orderDate', t('manager.modules.orderHistory.columns.orderDate', {}, 'Ordredato'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    lineColumnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.orderHistory.columns.deliveryDate', {}, 'Leveringsdato'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    })
  ], [statusOptions, t]);

  // Render order lines detail
  const renderOrderLines = useCallback((order: OrderHistoryTableRow) => {
    const lines = order.lines;
    const texts = order.orderTexts;

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        {/* Order Lines */}
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>{t('manager.modules.orderHistory.detail.orderLines', { '0': lines.length }, `Ordrelinjer (${lines.length})`)}</span>
        </h4>

        {lines.length > 0 ? (
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.columns.lineNo', {}, 'Linjenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.detail.itemNoShort', {}, 'Varenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.columns.itemName', {}, 'Varebeskrivelse')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.detail.ordered', {}, 'Bestilt')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.detail.picked', {}, 'Plukket')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.detail.delivered', {}, 'Levert')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.columns.unit', {}, 'Enhet')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.orderHistory.columns.operator', {}, 'Operatør')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {lines.map((line) => (
                <tr key={line.orderLineNo} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900">{line.orderLineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-medium font-mono">{line.itemNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.itemName || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyOrdered?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.qtyPicked?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyDelivered?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.unit || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.locationNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.operatorName || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm mb-4">
            {t('manager.modules.orderHistory.detail.noOrderLines', {}, 'Ingen ordrelinjer')}
          </div>
        )}

        {/* Order Texts */}
        {texts.length > 0 && (
          <>
            <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
              <FileText size={16} />
              <span>{t('manager.modules.orderHistory.detail.orderTexts', { '0': texts.length }, `Ordretekster (${texts.length})`)}</span>
            </h4>
            <div className="bg-white rounded border border-slate-200 p-3 max-h-32 overflow-y-auto">
              {texts.map((text, idx) => (
                <div key={`${text.textNo}-${text.textLine}`} className="text-xs text-slate-700 mb-1">
                  <span className="font-medium text-slate-500 mr-2">
                    [{text.textNo}.{text.textLine}]
                  </span>
                  {text.text}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }, [t]);

  // Expandable configuration for orders
  const expandableConfig: ExpandableRowConfig<OrderHistoryTableRow, null> = useMemo(() => ({
    getDetailData: () => null,
    renderDetail: renderOrderLines,
    expandOnRowClick: false
  }), [renderOrderLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: fetchData,
      variant: 'secondary' as const
    }
  ], [fetchData, t]);

  // Date filter toolbar component
  const dateFilterToolbar = useMemo(() => (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Calendar size={18} className="text-blue-500" />
        <label className="text-sm font-medium text-gray-700">{t('manager.modules.orderHistory.filters.from', {}, 'Fra:')}</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            if (e.target.value) {
              setFromDate(e.target.value);
            }
          }}
          className="px-3 py-1.5 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">{t('manager.modules.orderHistory.filters.to', {}, 'Til:')}</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            if (e.target.value) {
              setToDate(e.target.value);
            }
          }}
          className="px-3 py-1.5 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
        />
      </div>
      {dateError && (
        <div className="flex items-center text-red-600 text-sm">
          <AlertCircle size={16} className="mr-1" />
          {dateError}
        </div>
      )}
      <div className="text-sm text-gray-600 font-medium">
        {viewMode === 'master-detail'
          ? t('manager.modules.orderHistory.filters.orderCount', { '0': orderTableData.length }, `${orderTableData.length} ordre`)
          : t('manager.modules.orderHistory.filters.lineCount', { '0': lineTableData.length }, `${lineTableData.length} ordrelinjer`)}
      </div>
    </div>
  ), [fromDate, toDate, dateError, viewMode, orderTableData.length, lineTableData.length, t]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <BackButton onClick={onBack || (() => {})} />
            <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
              <Package size={24} />
              <span>{t('manager.modules.orderHistory.title', {}, 'Ordrehistorikk')}</span>
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* View mode toggle */}
            <button
              onClick={() => setViewMode('master-detail')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                viewMode === 'master-detail'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutGrid className="mr-2" size={18} />
              {t('manager.modules.orderHistory.viewMode.orders', {}, 'Ordre')}
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                viewMode === 'flat'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutList className="mr-2" size={18} />
              {t('manager.modules.orderHistory.viewMode.orderLines', {}, 'Ordrelinjer')}
            </button>
          </div>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 min-h-0 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader className="animate-spin" size={20} />
              <span>{t('manager.modules.orderHistory.messages.loading', {}, 'Laster ordrehistorikk...')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
            <h3 className="text-red-800 font-medium flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {t('manager.modules.orderHistory.errors.loadFailed', {}, 'Feil ved lasting av ordrehistorikk')}
            </h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={fetchData}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>{t('manager.modules.orderHistory.buttons.retry', {}, 'Prøv igjen')}</span>
            </button>
          </div>
        ) : viewMode === 'master-detail' ? (
          <WarehouseTable
            data={orderTableData}
            columns={orderColumns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'order-history-table'
            }}
            getRowId={getOrderRowId}
            tableActions={tableActions}
            expandableConfig={expandableConfig}
            toolbarLeft={dateFilterToolbar}
            loading={false}
            error={undefined}
            aria-label={t('manager.modules.orderHistory.aria.ordersTable', {}, 'Ordrehistorikk tabell')}
          />
        ) : (
          <WarehouseTable
            data={lineTableData}
            columns={lineColumns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'order-history-flat-table'
            }}
            getRowId={getLineRowId}
            tableActions={tableActions}
            toolbarLeft={dateFilterToolbar}
            loading={false}
            error={undefined}
            aria-label={t('manager.modules.orderHistory.aria.orderLinesTable', {}, 'Ordrelinjer historikk tabell')}
          />
        )}
      </div>

      {/* Order Timeline Modal */}
      {timelineOrderNo !== null && (
        <OrderTimelineModal
          isOpen={true}
          orderNo={timelineOrderNo}
          onClose={() => setTimelineOrderNo(null)}
        />
      )}
    </div>
  );
};

export default OrderHistory;
